import { ODBService } from '../services/odb.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Output, EventEmitter, Directive, Injectable } from '@angular/core';
import * as moment from 'moment';
import * as crypto from 'crypto-js';
import { SNItemFactory } from './snitemfactory';
import { SNUser } from './snuser';
import { CNHealthAgent } from './cnhealthagent';
import { CNUser } from './cnuser';
import { CNDoctor } from './cndoctor';
import { SNService } from './snservice';

export class SNItemAccess {
    constructor(
        public userid: number,
        public firstname: string,
        public lastname: string,
        public read: boolean,
        public write: boolean,
        public locked: boolean,
        public change: boolean,
        public readversion: number
    ) {}
}

@Directive()
// tslint:disable-next-line:directive-class-suffix
export class SNItem {
    public id = 0;
    public version = 0;
    public key = 'SNItem';
    public value = '';
    public createddate: Date;
    public modifieddate: Date;
    public creatorid = 0;
    public properties: any = {};
    private itemchildsbykey: Map<string, Array<any>> = new Map();
    private itemchilds: Array<any> = [];
    private itemparentsbykey: Map<string, SNItem[]> = new Map();
    private itemparents: SNItem[] = [];

    private itemLabel: string;
    private itemService: string;

    protected itemchecksum = '';
    protected modified = false;
    protected rawJson: any;
    protected odb: ODBService;
    protected access: SNItemAccess[];
    protected accessUsers: SNUser | CNUser | CNHealthAgent | CNDoctor[];

    @Output() created: EventEmitter<SNItem> = new EventEmitter();
    @Output() updated: EventEmitter<SNItem> = new EventEmitter();
    @Output() loaded: EventEmitter<SNItem> = new EventEmitter();
    @Output() childAdded: EventEmitter<SNItem> = new EventEmitter();
    @Output() childsLoaded: EventEmitter<SNItem[]> = new EventEmitter();
    @Output() parentAdded: EventEmitter<SNItem> = new EventEmitter();

    constructor(itemid: number = 0) {
        this.odb = ODBService.getInstance();
        this.setId(itemid);
    }

    public setId(itemid: number = 0) {
        // TODO: Really build this item
        this.id = itemid;
        if (this.id !== 0) {
            // Call odb service to get this item JSON
        }
    }

    public register(label?: string, service?: string) {
        if (label) {
            this.itemLabel = label;
        }
        if (service) {
            this.itemService = service;
        }
    }

    get isModified(): boolean {
        return this.modified;
    }

    get creator(): SNUser {
        return this.accessUsers ? (this.accessUsers as SNUser[]).find(user => user.id === this.creatorid) : undefined;
    }

    get checksumChain(): string {
        let propChain = this.value;
        Object.keys(this.properties).forEach(key => {
            propChain += this.properties[key];
        });
        this.childs$().forEach(child => {
            propChain += child.checksumChain;
        });
        // TODO: handle parents if needed
        return propChain;
    }

    get checksum(): string {
        return crypto.MD5(this.checksumChain).toString(crypto.enc.Base64);
    }

    checkModified(): boolean {
        const currentChecksum = this.checksum;
        if (currentChecksum !== this.itemchecksum) {
            this.modified = true;
        }
//        console.log('item ' + this.id + ' ' + this.key + ' checksum, current', this.itemchecksum, currentChecksum);
        return this.modified;
    }

    public reload() {
        this.odb.itemReload(this).subscribe();
    }

    public setValue(value: string) {
        this.value = value;
//        this.checkModified();
    }

    public setProperty(key: string, value: string | number) {
        if (typeof(value) === 'number') {
            this.properties[key] = value.toString();
        } else {
            this.properties[key] = value;
        }
//        this.checkModified();
    }

    public getProperty(key: string) {
        if (typeof this.properties[key] !== 'undefined') {
            return this.properties[key];
        }
        return null;
    }

    public propertyEmpty(key: string) {
        if (typeof this.properties[key] !== 'undefined') {
            if (this.properties[key].length !== 0) {
                return false;
            }
        }
        return true;
    }

    public childs<T extends SNItem>(childskey: string, subclasses?: boolean): Observable<Array<T>> {
        return new Observable(observer => {
            if (subclasses && subclasses === true) {
                // Get all subclasses
                const allKeys = [...this.itemchildsbykey.keys()];
                const subclasskeys = allKeys.filter((akey) => akey.startsWith(childskey));
                if (subclasskeys.length !== 0) {
                    const subclasschilds: Array<T> = [];
                    subclasskeys.forEach(subclasskey => {
                        subclasschilds.push(...this.itemchildsbykey.get(subclasskey));
                    });
                    observer.next(subclasschilds);
                } else {
                    this.odb.childs<T>(this, childskey, subclasses).subscribe(childs => {
                        childs.forEach(child => {
                            if (!this.itemchildsbykey.has(child.key)) {
                                this.itemchildsbykey.set(child.key, []);
                            }
                            this.itemchildsbykey.get(child.key).push(child);
                            if (this.itemchilds.indexOf(child) === -1) {
                                this.itemchilds.push(child);
                                // Link with child
                                child.addParent(this, false).subscribe();
                            }
                        });
                        this.childsLoaded.emit(childs);
                        observer.next(childs);
                    }, error => {
                        observer.error(error);
                    });
                }
            } else {
                if (this.itemchildsbykey.has(childskey)) {
                    observer.next(this.itemchildsbykey.get(childskey));
                } else {
                    this.odb.childs<T>(this, childskey).subscribe(childs => {
                        this.itemchildsbykey.set(childskey, childs);
                        childs.forEach(child => {
                            if (this.itemchilds.indexOf(child) === -1) {
                                this.itemchilds.push(child);
                                // Link with child
                                child.addParent(this, false).subscribe();
                            }
                        });
                        this.childsLoaded.emit(childs);
                        observer.next(childs);
                    }, error => {
                        observer.error(error);
                    });
                }
            }
        });
    }
    /**
     * Special method to get childs from cache
     * @param childskey the child key to search for
     */
    public childs$(childskey?: string): SNItem[] {
        if (!!childskey) {
            if (this.itemchildsbykey.has(childskey)) {
                return this.itemchildsbykey.get(childskey);
            }
            return [];
        }
        return this.itemchilds;
    }
    /**
     * Cpecial method to get the first child in cache
     * @param childkey the child key
     */
    public child$(childkey: string): SNItem {
        if (!this.itemchildsbykey.has(childkey)) {
            return null;
        }
        return this.itemchildsbykey.get(childkey)[0];
    }
    /**
     * Special method to check item in cache
     * @param childskey the key to check
     */

     public hasChilds$(childskey: string): boolean {
         return this.itemchildsbykey.has(childskey);
     }

    public addChild(child: SNItem, now = true): Observable<SNItem> {
        if (now) {
            return new Observable(observer => {
                this.odb.addChild(this, child).subscribe(newchild => {
                    this.updateChildsCache(newchild);
                    newchild.updateParentsCache(this);
                    this.childAdded.emit(newchild);
                    observer.next(newchild);
                });
            });
        }
        this.updateChildsCache(child);
        child.updateParentsCache(this);
        this.childAdded.emit(child);
        return new Observable(observer => {
            observer.next(child);
        });
    }

    public parents(parentskey: string): Observable<SNItem[]> {
        if (this.itemparentsbykey.has(parentskey)) {
            return new Observable(observer => {
                observer.next(this.itemparentsbykey.get(parentskey));
            });
        }
        return this.odb.parents(this, parentskey).pipe(map(parents => {
            this.itemparentsbykey.set(parentskey, parents);
            parents.forEach(parent => {
                if (this.itemparents.indexOf(parent) === -1) {
                    this.itemparents.push(parent);
                    parent.addChild(this, false);
                }
            });
            return parents;
        }));
    }
    /**
     * Special method to get parents from cache
     * @param parentskey the parent key to search for
     */
    public parents$(parentskey?: string): SNItem[] {
        if (!!parentskey) {
            if (this.itemparentsbykey.has(parentskey)) {
                return this.itemparentsbykey.get(parentskey);
            }
            return [];
        }
        return this.itemparents;
    }

    public addParent(parent: SNItem, now = true): Observable<SNItem> {
        if (now) {
            return this.odb.addParent(this, parent).pipe(map(newparent => {
                this.updateParentsCache(newparent);
                newparent.updateChildsCache(this);
                this.parentAdded.emit(newparent);
                return newparent;
            }));
        }
        this.updateParentsCache(parent);
        parent.updateChildsCache(this);
        return new Observable(observer => {
            observer.next(parent);
        });
    }

    public create(): Observable<SNItem> {
        return this.odb.createItem(this).pipe(map(newitem => {
            this.created.emit(newitem);
            this.itemchecksum = this.checksum;
            console.log('item ' + this.id + ' ' + this.key + ' checksum', this.itemchecksum);
            return newitem;
        }));
    }

    public removeChild(child: SNItem, link?: boolean): Observable<SNItem> {
        return new Observable(observer => {
            this.odb.removeChild(this, child, link).subscribe(removed => {
                if (this.itemchilds.length === 0) {
                    observer.next(null);
                } else {
                    // update cache
                    let foundItem = false;
                    for (let i = 0; i < this.itemchilds.length; i++) {
                        const achild = this.itemchilds[i];
                        if (achild.id === child.id) {
                            console.log('removing from cache id', achild.id);
                            this.itemchilds.splice(i, 1);
                            if (this.itemchildsbykey.has(child.key)) {
                                const childsofkey = this.itemchildsbykey.get(child.key);
                                console.log('removing from key cache', child.key, childsofkey);
                                for (let j = 0; j < childsofkey.length; j++) {
                                    const bchild = childsofkey[j];
                                    if (bchild.id === child.id) {
                                        console.log('removing from key cache id', bchild.id);
                                        childsofkey.splice(j, 1);
        //                                this.itemchildsbykey.set(child.key, childsofkey);
                                        observer.next(removed);
                                        foundItem = true;
                                    }
                                }
                            }
                        }
                    }
                    if (!foundItem) {
                        observer.next(null);
                    }
                }
            });
        });
    }

    public purgeChilds$(childkey: string): void {
        // Remove child in cache
        let indexToRemove: number[] = [];
        for (let i  = 0 ; i < this.itemchilds.length ; i++) {
            if (this.itemchilds[i].key === childkey && this.itemchilds[i].id === 0) {
                indexToRemove.push(i);
            }
        }
        indexToRemove.forEach(index => {
            this.itemchilds.splice(index, 1);
        });
        indexToRemove = [];
        if (this.itemchildsbykey.has(childkey)) {
            const childsofkey = this.itemchildsbykey.get(childkey);
            for (let i  = 0 ; i < childsofkey.length ; i++) {
                if (childsofkey[i].id === 0) {
                    indexToRemove.push(i);
                }
            }
            indexToRemove.forEach(index => {
                childsofkey.splice(index, 1);
            });
        }
    }

    private updateChildsCache(child: SNItem) {
        if (this.itemchildsbykey.has(child.key)) {
            // Append
            const keyArray = this.itemchildsbykey.get(child.key);
            if (keyArray.indexOf(child) === -1) {
                if (child.id !== 0) {
                    // try by id
                    const childById = keyArray.find(item => item.id === child.id);
                    if (childById) {
                        // Found child by id, remove previous, add this one
                        keyArray.splice(keyArray.indexOf(childById), 1);
                    }
                }
                keyArray.push(child);
            }
        } else {
            this.itemchildsbykey.set(child.key, [child]);
        }
        if (this.itemchilds.indexOf(child) === -1) {
            if (child.id !== 0) {
                // try by id
                const childById = this.itemchilds.find(item => item.id === child.id);
                if (childById) {
                    // Found child by id, remove previous, add this one
                    this.itemchilds.splice(this.itemchilds.indexOf(childById), 1);
                }
            }
            this.itemchilds.push(child);
        }
    }

    private updateParentsCache(parent: SNItem) {
        if (this.itemparentsbykey.has(parent.key)) {
            // Append
            const keyArray = this.itemparentsbykey.get(parent.key);
            if (keyArray.indexOf(parent) === -1) {
                keyArray.push(parent);
            }
            this.itemparentsbykey.get(parent.key).push(parent);
        } else {
            this.itemparentsbykey.set(parent.key, [parent]);
        }
        if (this.itemparents.indexOf(parent) === -1) {
            this.itemparents.push(parent);
        }
    }

    public update(): Observable<SNItem> {
        return new Observable(observer => {
            this.odb.update(this).subscribe(updateditem => {
                this.updated.emit(updateditem);
                this.itemchecksum = this.checksum;
                console.log('item ' + this.id + ' ' + this.key + ' checksum', this.itemchecksum);
                observer.next(updateditem);
            });
        });
    }

    public clone(): any {
        return new SNItem();
    }

    public copy(withid = false, deep = false) {
        const itemcopy = this.clone();
        itemcopy.id = withid ? this.id : 0;
        itemcopy.key = this.key;
        itemcopy.value = this.value;
        itemcopy.version = withid ? this.version : 0;
        itemcopy.properties = Object.assign([], this.properties);
        if (deep) {
            this.itemchilds.forEach(child => {
                itemcopy.addChild(child.copy(withid, deep), false);
            });
        }
        return itemcopy;
    }

    public get className(): string {
        // return real class name
        return this.constructor.name;
    }

    public get json(): any {
        return this.rawJson;
    }

    public fromJson(json, emitLoaded = true) {
        this.rawJson = json;
        if (typeof json.Key === 'undefined') {
            if (typeof json.key === 'undefined') {
                console.log(json);
                return;
            } else {
                // item from snitem.ts
                if (this.key !== 'SNItem' && this.key !== json.key) {
                    // Only for snitem subclasses
                    console.log('Wrong key');
                } else {
                    this.id = parseInt(json.id, 10);
                    this.key = json.key;
                    this.value = json.value;
                    this.version = parseInt(json.version, 10);
                    this.properties = json.properties;
                    this.createddate = moment(json.createddate).toDate();
                    this.modifieddate = moment(json.modifieddate).toDate();
                    this.creatorid = parseInt(json.creatorid, 10);
                    if (typeof json.childs !== 'undefined') {
                        // Build childs
                        const factory = SNItemFactory.getInstance();
                        const childs = factory.itemsFromJson(json.childs);
                        if (childs) {
                            childs.forEach(aChild => {
                                this.addChild(aChild, false);
                            });
                        }
                    }
                    if (emitLoaded) {
                        this.loaded.emit(this);
                    }
                }
            }
        } else {
            if (this.key !== 'SNItem' && this.key !== json.Key) {
                // Only for snitem subclasses
                console.log('Wrong key');
            } else {
                this.id = parseInt(json.ID, 10);
                this.key = json.Key;
                this.value = json.Value;
                this.version = parseInt(json.Version, 10);
                this.properties = json.properties;
                this.createddate = moment(json.CreatedDate).toDate();
                this.modifieddate = moment(json.ModifiedDate).toDate();
                this.creatorid = parseInt(json.CreatorID, 10);
                if (typeof json.childs !== 'undefined') {
                    // Build childs
                    const factory = SNItemFactory.getInstance();
                    const childs = factory.itemsFromJson(json.childs);
                    if (childs) {
                        childs.forEach(aChild => {
                            this.addChild(aChild, false);
                        });
                    }
                }
                if (emitLoaded) {
                    this.loaded.emit(this);
                }
            }
        }
        if (typeof json.access !== 'undefined' && Array.isArray(json.access) && json.access.length !== 0) {
            this.access = [];
            json.access.forEach(access => {
                this.access.push(new SNItemAccess(access.userid, '', '', access.read, access.write, false, false, -1));
            });
        }
        if (typeof json.users !== 'undefined' && Array.isArray(json.users) && json.users.length !== 0) {
            this.accessUsers = SNItemFactory.getInstance().itemsFromJson(json.users);
        }
        this.itemchecksum = this.checksum;
        // console.log('item ' + this.id + ' ' + this.key + ' checksum', this.itemchecksum);
    }

    public toObj(deep = false): any {
        const itemObj = {
            id: this.id,
            version: this.version,
            key: this.key,
            value: this.value,
            properties: this.properties,
            childs: undefined
        };
        if (deep) {
            itemObj.childs = [];
            const allChilds = this.childs$();
            allChilds.forEach(achild => {
                itemObj.childs.push(achild.toObj());
            });
        }
        return itemObj;
    }

    public toJson(deep = false) {
        return JSON.stringify(this.toObj(deep));
    }
}
